// Pure, framework-free business logic mirrored from the Postgres functions
// in supabase/migrations/. The database is the source of truth and
// re-validates everything server-side -- these exist so the UI can render
// instant, correct feedback (occupancy badges, capacity math, summaries)
// without a round-trip, and so the rules have unit tests independent of a
// live database (see tests/domain).

import type { OccupancyStatus, PortalContext, PortalHotel } from '../types'

export function roomOccupancyStatus(room: { capacity: number; guests: { full_name: string | null }[] }): OccupancyStatus {
  const filled = room.guests.filter((g) => g.full_name != null && g.full_name !== '').length
  if (filled === 0) return 'empty'
  if (filled >= room.capacity) return 'full'
  return 'partial'
}

export function filledSlotCount(room: { guests: { full_name: string | null }[] }): number {
  return room.guests.filter((g) => g.full_name != null && g.full_name !== '').length
}

/**
 * Number of nights between two plain (date-only, e.g. "2026-10-12") dates.
 * Parses them as UTC calendar dates rather than local-time instants, so the
 * result is never off by one depending on the browser's timezone. Returns
 * null when either date is missing, or when check-out isn't after check-in.
 */
export function nightsBetween(checkIn: string | null | undefined, checkOut: string | null | undefined): number | null {
  if (!checkIn || !checkOut) return null
  const inMs = Date.parse(`${checkIn}T00:00:00Z`)
  const outMs = Date.parse(`${checkOut}T00:00:00Z`)
  if (Number.isNaN(inMs) || Number.isNaN(outMs)) return null
  const nights = Math.round((outMs - inMs) / 86400000)
  return nights > 0 ? nights : null
}

/** Sums nightsBetween(...) across every room in a hotel/room-type tree, skipping rooms with no (or invalid) dates. Used for the admin-only "Total nights" figure. */
export function totalNights(hotels: { room_types: { rooms: { check_in: string | null; check_out: string | null }[] }[] }[]): number {
  let sum = 0
  for (const hotel of hotels) {
    for (const rt of hotel.room_types) {
      for (const room of rt.rooms) {
        sum += nightsBetween(room.check_in, room.check_out) || 0
      }
    }
  }
  return sum
}

/** Same rule as roomOccupancyStatus, for callers that already have a flat (filled, capacity) pair rather than a nested guests[] array -- e.g. rows from admin_export_rows. */
export function roomOccupancyStatusFromCounts(filled: number, capacity: number): OccupancyStatus {
  if (filled <= 0) return 'empty'
  if (filled >= capacity) return 'full'
  return 'partial'
}

export interface HotelSummaryRow {
  hotelId: string
  hotelName: string
  roomTypeId: string
  roomTypeName: string
  allocatedRooms: number
  occupiedRooms: number
  fullRooms: number
  partialRooms: number
  emptyRooms: number
  capacity: number
  guests: number
  remainingSlots: number
}

/** Rolls a portal (or admin rooming-list) hotel tree up into per hotel/room-type rows, matching section 21/97's "Hilton / Single: 8/10 rooms occupied" style summaries. */
export function summarizeByHotelAndRoomType(hotels: PortalHotel[]): HotelSummaryRow[] {
  const rows: HotelSummaryRow[] = []
  for (const hotel of hotels) {
    for (const rt of hotel.room_types) {
      let occupied = 0
      let full = 0
      let partial = 0
      let empty = 0
      let capacity = 0
      let guests = 0
      for (const room of rt.rooms) {
        const status = roomOccupancyStatus(room)
        capacity += room.capacity
        guests += filledSlotCount(room)
        if (status === 'empty') empty++
        else {
          occupied++
          if (status === 'full') full++
          else partial++
        }
      }
      rows.push({
        hotelId: hotel.id,
        hotelName: hotel.name,
        roomTypeId: rt.id,
        roomTypeName: rt.name,
        allocatedRooms: rt.rooms.length,
        occupiedRooms: occupied,
        fullRooms: full,
        partialRooms: partial,
        emptyRooms: empty,
        capacity,
        guests,
        remainingSlots: capacity - guests,
      })
    }
  }
  return rows
}

export function portalSummaryFromContext(ctx: PortalContext) {
  return ctx.summary
}

/** Mirrors normalize_mobile() in Postgres -- Egypt-tuned, degrades gracefully for other formats. Used only for instant client-side duplicate hints; the DB is authoritative. */
export function normalizeMobile(input: string | null | undefined): string | null {
  if (!input) return null
  let digits = input.replace(/\D/g, '')
  if (!digits) return null
  if (digits.startsWith('00')) digits = digits.slice(2)

  // Egypt country code (20) can be typed with or without the local trunk 0
  // that follows it ("+20 1012345678" and "+20 01012345678" both happen in
  // the wild) -- normalize both to the same local "0..." form.
  if (digits.startsWith('20') && digits.length >= 11) {
    const rest = digits.slice(2)
    if (rest.startsWith('0')) {
      digits = rest
    } else if (rest[0] === '1' && ['0', '1', '2', '5'].includes(rest[1])) {
      digits = '0' + rest
    }
  }

  if (digits.length === 10 && digits[0] === '1' && ['0', '1', '2', '5'].includes(digits[1])) {
    digits = '0' + digits
  }
  return digits
}

export function roomLabel(roomTypeName: string, labelPrefix: string | null | undefined, sequence: number): string {
  const prefix = (labelPrefix || roomTypeName).replace(/[^A-Za-z0-9]+/g, '').toUpperCase()
  return `${prefix}-${String(sequence).padStart(3, '0')}`
}

export interface AllocationDraft {
  roomCount: number
  capacityPerRoom: number
}

/** Total guest capacity for one allocation row (room count x capacity/room), kept as a named helper so "room count" and "guest capacity" are never conflated anywhere in the UI (spec section 92). */
export function allocationCapacity(a: AllocationDraft): number {
  return a.roomCount * a.capacityPerRoom
}
